import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { useUpdateCommunityRules } from '@/api/endpoints/community/useUpdateCommunityRules';

import FormTextField from '@/components/form/FormTextField';
import ModalWithForm from '@/components/modals/ModalWithForm';

interface EditRulesRequest {
  rules: string;
}

interface EditRulesModalProps {
  communityId: number;
  currentRules: string | null;
  onClose: () => void;
}

const EditRulesModal: React.FC<EditRulesModalProps> = ({
  communityId,
  currentRules,
  onClose,
}) => {
  const form = useForm<EditRulesRequest>({
    defaultValues: {
      rules: currentRules || '',
    },
    mode: 'onBlur',
  });

  const {
    register,
    control,
    formState: { errors },
  } = form;

  const updateRulesMutation = useUpdateCommunityRules({
    onSuccess: () => {
      onClose();
    },
  });

  const onSubmit = useCallback(
    async (data: EditRulesRequest) => {
      await updateRulesMutation.mutateAsync({
        communityId,
        rules: data.rules.trim() || null,
      });
    },
    [updateRulesMutation, communityId]
  );

  return (
    <ModalWithForm
      form={form}
      onSubmit={onSubmit}
      onClose={onClose}
      maxWidth="md"
      title="Edit Community Rules"
      buttonLabel="Save Rules"
    >
      <FormTextField
        control={control}
        label="Community Rules"
        size="small"
        multiline
        rows={8}
        placeholder="Enter community rules and guidelines for members to follow..."
        helperText={
          errors.rules?.message || 
          'Set clear guidelines for community members. Leave empty to remove all rules.'
        }
        error={!!errors.rules}
        {...register('rules', {
          maxLength: {
            value: 2000,
            message: 'Rules cannot exceed 2000 characters',
          },
        })}
      />
    </ModalWithForm>
  );
};

export default EditRulesModal;